// New-tenant wizard: the draft, its validation and the request it becomes.

import type { AddonMap, CreateTenantRequest } from '@/api/types'
import { PACKAGE_ADDON_DEFAULTS, addonsDifferingFrom } from '@/lib/addons'

export const STEPS = ['Name & subdomain', 'Sport', 'First admin', 'Plan & trial'] as const
export type StepIndex = 0 | 1 | 2 | 3

/** Same rule as the server's SUBDOMAIN_RE: 1–63 chars, a–z 0–9 and inner hyphens. */
export const SUBDOMAIN_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
export const MAX_TRIAL_DAYS = 180

export type PackageChoice = 'standard' | 'none'

export interface Draft {
  name: string
  /** What the person typed; empty + untouched means "follow the name". */
  subdomain: string
  subdomainTouched: boolean
  sport: string
  skipAdmin: boolean
  /** The first admin, invited by email after the create (they set their own password). */
  adminEmail: string
  trialDays: string
  packageKey: PackageChoice
  addons: AddonMap
  /** Once an add-on is flipped by hand, changing the package stops resetting them. */
  addonsTouched: boolean
  notes: string
}

const NONE: AddonMap = { scorebook: false, broadcast: false, fan_ai: false, ticketing: false, white_label: false, registration: false }

export function packageAddons(pkg: PackageChoice): AddonMap {
  return pkg === 'standard' ? { ...(PACKAGE_ADDON_DEFAULTS.standard ?? NONE) } : { ...NONE }
}

export function emptyDraft(): Draft {
  return {
    name: '',
    subdomain: '',
    subdomainTouched: false,
    sport: 'basketball',
    skipAdmin: false,
    adminEmail: '',
    trialDays: '14',
    packageKey: 'standard',
    addons: packageAddons('standard'),
    addonsTouched: false,
    notes: '',
  }
}

export function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
    .replace(/-+$/g, '')
}

export function effectiveSubdomain(d: Pick<Draft, 'name' | 'subdomain' | 'subdomainTouched'>): string {
  return d.subdomainTouched ? d.subdomain.trim().toLowerCase() : slugify(d.name)
}

export function trialDaysError(raw: string): string | null {
  const n = Number(raw)
  if (!raw.trim() || !Number.isInteger(n)) return 'Enter a whole number of days.'
  if (n < 1 || n > MAX_TRIAL_DAYS) return `A trial is 1–${MAX_TRIAL_DAYS} days.`
  return null
}

export function adminEmailError(d: Pick<Draft, 'skipAdmin' | 'adminEmail'>): string | null {
  if (d.skipAdmin) return null
  if (!d.adminEmail.trim()) return 'Enter the email to invite, or skip this step.'
  return EMAIL_PATTERN.test(d.adminEmail.trim()) ? null : 'That doesn’t look like an email address.'
}

export function toRequest(d: Draft): CreateTenantRequest {
  // The server pins every add-on key it's sent, so send only the ones that
  // differ from the chosen package; the rest keep following the package.
  const defaults = packageAddons(d.packageKey)
  const addons = Object.fromEntries(addonsDifferingFrom(d.addons, defaults).map((k) => [k, d.addons[k]]))
  return {
    name: d.name.trim(),
    subdomain: effectiveSubdomain(d),
    default_sport_id: d.sport,
    // The first admin is invited by email after the league exists
    // (NewTenantPage), so the create itself never carries one: the route and
    // its other callers are unchanged.
    admin: null,
    trial_days: Number(d.trialDays),
    package_key: d.packageKey === 'standard' ? 'standard' : null,
    ...(Object.keys(addons).length ? { addons } : {}),
    notes: d.notes.trim() || null,
  }
}

/** Which step owns a request field the server rejected. */
export function stepForField(field: string | undefined): StepIndex | null {
  if (!field) return null
  const root = field.split(/[.[]/)[0]
  if (root === 'name' || root === 'subdomain' || root === 'description') return 0
  if (root === 'default_sport_id' || root === 'sport') return 1
  if (root === 'admin' || root === 'email') return 2
  if (root === 'trial_days' || root === 'package_key' || root === 'addons' || root === 'notes') return 3
  return null
}

/** The draft key that shows a server error for a request field. */
export function draftKeyForField(field: string | undefined): keyof Draft | null {
  if (!field) return null
  if (field === 'name') return 'name'
  if (field === 'subdomain') return 'subdomain'
  if (field === 'default_sport_id') return 'sport'
  if (field.startsWith('admin') || field === 'email') return 'adminEmail'
  if (field === 'trial_days') return 'trialDays'
  if (field === 'package_key') return 'packageKey'
  if (field.startsWith('addons')) return 'addons'
  if (field === 'notes') return 'notes'
  return null
}

/** The address the wizard invites as the first league admin, or null when skipped. */
export function inviteEmail(d: Pick<Draft, 'skipAdmin' | 'adminEmail'>): string | null {
  return d.skipAdmin ? null : d.adminEmail.trim().toLowerCase() || null
}
